// Currency Conversion Utilities
import { CurrencyCode } from '../types/region';
import { EXCHANGE_RATES, CURRENCIES, REGIONAL_PRICING_MULTIPLIERS, REGIONS } from './currencyData';
import type { RegionCode } from '../types/region';

/**
 * Convert amount from INR (base currency) to target currency
 * @param amountInINR - Amount in Indian Rupees
 * @param targetCurrency - Target currency code
 * @returns Converted amount
 */
export function convertFromINR(amountInINR: number, targetCurrency: CurrencyCode): number {
  if (targetCurrency === 'INR') {
    return amountInINR;
  }
  
  const rate = EXCHANGE_RATES[targetCurrency];
  if (!rate) {
    console.warn(`No exchange rate found for ${targetCurrency}, defaulting to INR`);
    return amountInINR;
  }
  
  return amountInINR * rate;
}

/**
 * Convert amount from any currency to INR (base currency)
 * @param amount - Amount in source currency
 * @param sourceCurrency - Source currency code
 * @returns Amount in INR
 */
export function convertToINR(amount: number, sourceCurrency: CurrencyCode): number {
  if (sourceCurrency === 'INR') {
    return amount;
  }
  
  const rate = EXCHANGE_RATES[sourceCurrency];
  if (!rate) {
    console.warn(`No exchange rate found for ${sourceCurrency}, returning original amount`);
    return amount;
  }
  
  return amount / rate;
}

/**
 * Convert between any two currencies
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency
 * @param toCurrency - Target currency
 * @returns Converted amount
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): number {
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  // Convert to INR first, then to target currency
  const amountInINR = convertToINR(amount, fromCurrency);
  return convertFromINR(amountInINR, toCurrency);
}

/**
 * Apply regional pricing adjustments
 * @param amount - Base amount
 * @param regionCode - Region code
 * @returns Adjusted amount based on regional pricing strategy
 */
export function applyRegionalPricing(amount: number, regionCode: RegionCode): number {
  const multiplier = REGIONAL_PRICING_MULTIPLIERS[regionCode] || 1.0;
  return amount * multiplier;
}

/**
 * Format currency amount according to locale and currency settings
 * @param amount - Amount to format
 * @param currencyCode - Currency code
 * @param showSymbol - Whether to show currency symbol
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currencyCode: CurrencyCode,
  showSymbol: boolean = true
): string {
  const currency = CURRENCIES[currencyCode];
  if (!currency) {
    console.warn(`Currency ${currencyCode} not found, using fallback formatting`);
    return amount.toFixed(2);
  }
  
  // Round the amount based on currency decimal places
  const roundedAmount = Math.round(amount * Math.pow(10, currency.decimalPlaces)) / Math.pow(10, currency.decimalPlaces);
  
  try {
    const formatted = new Intl.NumberFormat(currency.locale, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: currency.code,
      minimumFractionDigits: currency.decimalPlaces,
      maximumFractionDigits: currency.decimalPlaces,
    }).format(roundedAmount);
    
    return formatted;
  } catch (error) {
    console.warn('Error formatting currency:', error);
    // Fallback formatting
    return `${showSymbol ? currency.symbol : ''}${roundedAmount.toFixed(currency.decimalPlaces)}`;
  }
}

/**
 * Get price in target region/currency
 * @param basePriceINR - Base price in INR
 * @param targetRegion - Target region code
 * @returns Object with converted price and currency
 */
export function getPriceForRegion(basePriceINR: number, targetRegion: RegionCode): {
  amount: number;
  currency: CurrencyCode;
  formatted: string;
} {
  const region = REGIONS[targetRegion];
  if (!region) {
    console.warn(`Region ${targetRegion} not found, using INR`);
    return {
      amount: basePriceINR,
      currency: 'INR',
      formatted: formatCurrency(basePriceINR, 'INR')
    };
  }
  
  // Convert to target currency
  let convertedAmount = convertFromINR(basePriceINR, region.currency);
  
  // Apply regional pricing adjustments
  convertedAmount = applyRegionalPricing(convertedAmount, targetRegion);
  
  // Round to "nice" numbers for better UX
  convertedAmount = roundToNiceNumber(convertedAmount, region.currency);
  
  return {
    amount: convertedAmount,
    currency: region.currency,
    formatted: formatCurrency(convertedAmount, region.currency)
  };
}

/**
 * Round to psychologically appealing price points
 * @param amount - Original amount
 * @param currency - Currency code
 * @returns Rounded amount
 */
function roundToNiceNumber(amount: number, currency: CurrencyCode): number {
  // Return the exact amount without aggressive rounding
  // This preserves psychological pricing like 499, 999
  return Math.round(amount);
}

/**
 * Detect user's region based on browser locale and timezone
 * @returns Detected region code (defaults to GLOBAL if cannot detect)
 */
export function detectUserRegion(): RegionCode {
  try {
    // Try to detect from browser locale
    const locale = navigator.language || 'en-US';
    
    // Simple locale to region mapping
    const localeMap: Record<string, RegionCode> = {
      'en-IN': 'IN',
      'hi-IN': 'IN',
      'en-US': 'US',
      'en-GB': 'GB',
      'en-AU': 'AU',
      'en-CA': 'CA',
      'en-SG': 'SG',
      'ar-AE': 'AE',
      'ms-MY': 'MY',
      // Fallback for common European locales
      'de-DE': 'EU',
      'fr-FR': 'EU',
      'es-ES': 'EU',
      'it-IT': 'EU',
    };
    
    const detectedRegion = localeMap[locale];
    if (detectedRegion) {
      return detectedRegion;
    }
    
    // Try to match just the country code
    const countryCode = locale.split('-')[1]?.toUpperCase();
    if (countryCode && countryCode in REGIONS) {
      return countryCode as RegionCode;
    }
    
    // Default to GLOBAL
    return 'GLOBAL';
  } catch (error) {
    console.warn('Error detecting user region:', error);
    return 'GLOBAL';
  }
}

/**
 * Get currency symbol for a given currency code
 * @param currencyCode - Currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(currencyCode: CurrencyCode): string {
  return CURRENCIES[currencyCode]?.symbol || currencyCode;
}

/**
 * Get currency name for a given currency code
 * @param currencyCode - Currency code
 * @returns Currency name
 */
export function getCurrencyName(currencyCode: CurrencyCode): string {
  return CURRENCIES[currencyCode]?.name || currencyCode;
}
